import { Client } from "@twilio/conversations";
import { Dispatch } from "redux";
import log from "loglevel";

import { initMessagesListener } from "./listeners/messagesListener";
import { initParticipantsListener } from "./listeners/participantsListener";
import { initConversationListener } from "./listeners/conversationListener";
import { ConfigState, EngagementPhase } from "../definitions";
import { initClientListeners } from "./listeners/clientListener";
import { notifications } from "../../notifications";
import { ACTION_START_SESSION, ACTION_LOAD_CONFIG } from "./actionTypes";
import { addNotification, changeEngagementPhase, updateWelcomeInputLock } from "./genericActions";
import { MESSAGES_LOAD_COUNT } from "../../constants";
import { reportClientError } from "../../utils/reportClientError";
import { descreverErro } from "../../utils/descreverErro";
import { extrairCodigos } from "../../utils/diagnosticoDeEnvio";

export function initConfig(config: ConfigState) {
    return {
        type: ACTION_LOAD_CONFIG,
        payload: config
    };
}

/**
 * Teto para o handshake do SDK.
 *
 * 📏 MEDIDO no N3 contra o Twilio real (2026-09-02, conta de teste, 4 amostras): a abertura completa
 * — `/initWebchat` + handshake + carga da conversa — levou 4,33s / 4,83s / 4,33s / 4,33s, com o
 * `/initWebchat` consumindo 2,41s a 2,87s. O handshake do SDK isolado ficou em ~1,3s.
 *
 * 20s são ~15x o observado, e a folga é deliberada: curto demais corta conexão lenta que IA
 * funcionar — pior que o bug original, porque atinge justamente quem está com rede ruim. Este teto
 * existe para o caso em que o SDK NÃO sinaliza nada (twilsock pendurado), não para apressar quem
 * está devagar. Se for mexer neste número, meça de novo — não estime.
 *
 * 🔴 Curto demais corta conexão lenta que ia funcionar — pior que o bug, porque atinge quem está bem.
 */
export const TIMEOUT_DE_INICIALIZACAO_MS = 20000;

/**
 * Espera o SDK sinalizar, com um TERCEIRO desfecho.
 *
 * 🔴 Antes eram só dois: `initialized` resolve, `initFailed` rejeita. Com o Twilsock travado (perda
 * parcial de rede) NENHUM dos dois dispara — e a promise ficava pendente para sempre. O apostador
 * olhava "Conectando você a um atendente…" indefinidamente: nenhum `catch`, nenhum ErrorBoundary,
 * nenhum `reportClientError`. Chat indisponível COM CARA de conexão em andamento, e a operação
 * sem saber que existiu.
 *
 * Esperar para sempre não é esperar, é travar. Ao estourar o teto, desliga o client best-effort
 * (senão fica socket vivo segurando closure) e rejeita com erro técnico fixo — sem texto do SDK,
 * porque o `reason` da telemetria não pode carregar dado do apostador.
 */
/** Carimba o nome técnico no erro; `descreverErro` só o preserva se tiver forma de identificador. */
const nomear = (erro: Error, nome: string): Error => {
    erro.name = nome;
    return erro;
};

const esperarInicializacao = async (client: Client): Promise<void> => {
    let expirou: ReturnType<typeof setTimeout> | undefined;
    try {
        await new Promise<void>((resolve, reject) => {
            expirou = setTimeout(() => {
                /*
                 * 🔴 REJEITA PRIMEIRO — a limpeza NÃO pode impedir o desfecho.
                 *
                 * Antes o `shutdown()` vinha antes do `reject()` e sem proteção contra exceção
                 * SÍNCRONA. Num client que nunca inicializou o estado interno do SDK está pela
                 * metade, e um `throw` ali dentro escapava do callback do `setTimeout`: o
                 * `reject()` nunca rodava, a promise ficava pendente PARA SEMPRE e o apostador
                 * voltava a ficar preso em "Conectando você a um atendente…" — exatamente o bug
                 * que este deadline existe para matar. A faxina passou a vir depois e blindada.
                 *
                 * O NOME é o discriminador e viaja sozinho: `initSession` RELANÇA daqui, e quem
                 * reporta são os chamadores (`WebchatWidget`, `PrechatForm`). Reportar aqui dentro
                 * duplicaria a telemetria do mesmo evento. Como `descreverErro` preserva `name`
                 * quando ele tem forma de identificador técnico, a operação vê
                 * "ClientHandshakeTimeout: falha técnica" e distingue Twilsock pendurado de token
                 * recusado, sem campo novo nenhum.
                 */
                reject(nomear(new Error("timeout ao inicializar o client do Twilio"), "ClientHandshakeTimeout"));
                try {
                    void (client as unknown as { shutdown?: () => Promise<void> })
                        .shutdown?.()
                        .catch(() => undefined);
                } catch {
                    /* best-effort: desligar é higiene nossa e nunca pode virar falha */
                }
            }, TIMEOUT_DE_INICIALIZACAO_MS);

            client.onceWithReplay(Client.initialized, () => resolve());
            /*
             * Sem destructuring no parâmetro: se o SDK emitir o evento sem payload, desestruturar
             * lançaria DENTRO do callback dele — e a rejeição, de novo, nunca aconteceria.
             */
            client.onceWithReplay(Client.initFailed, (payload) =>
                reject(payload?.error ?? nomear(new Error("client do Twilio não inicializou"), "ClientInitFailed"))
            );
        });
    } finally {
        // Sempre — inclusive no sucesso e no initFailed. Timer solto segura o processo no runner.
        if (expirou) clearTimeout(expirou);
    }
};

export function initSession({ token, conversationSid }: { token: string; conversationSid: string }) {
    return async (dispatch: Dispatch) => {
        let conversationsClient: Client;
        let conversation;
        let participants;
        let users;
        let messages;

        try {
            /*
             * `Client.create()` está @deprecated no SDK ("Call constructor directly") e, pela doc
             * do próprio Twilio, PERDE os eventos emitidos antes da inicialização — ele devolve o
             * client já pronto, então tudo que aconteceu no caminho passou batido.
             *
             * `new Client()` devolve o client AINDA NÃO inicializado. A garantia que o `create()`
             * dava escondida (só resolver depois de conectar) passa a ser explícita aqui: só
             * seguimos adiante quando o SDK sinalizar `initialized`, e propagamos o erro no
             * `initFailed` — mesmo comportamento observável de antes.
             *
             * `onceWithReplay` e NÃO `once`: se o evento disparar antes desta linha rodar, o
             * `once` o perderia e o widget ficaria preso em "Conectando você a um atendente…"
             * PARA SEMPRE, sem erro nenhum. O `WithReplay` reemite o último valor já emitido.
             */
            conversationsClient = new Client(token);
            await esperarInicializacao(conversationsClient);
            try {
                conversation = await conversationsClient.getConversationBySid(conversationSid);
            } catch (e) {
                /*
                 * Auditoria de observabilidade (2026-08-28): este caminho avisava o cliente e o
                 * devolvia ao formulário, mas não reportava NADA — o apostador era expulso de uma
                 * conversa que tinha e ninguém sabia, nem quantas vezes.
                 */
                reportClientError("session_init_failed", {
                    reason: descreverErro(e),
                    failureStage: "conversation_lookup",
                    ...extrairCodigos(e)
                });
                /*
                 * 🔴 CLIENT ÓRFÃO. O handshake deu certo, então este client tem socket aberto e
                 * timers do SDK rodando. Ele NÃO chega ao `ACTION_START_SESSION`, logo ninguém
                 * guarda a referência — nem o store, nem o `reconectar`, que só desliga o client
                 * que recebeu. Sem isto, cada tentativa na mesma página deixa mais um vivo até o
                 * apostador fechar a aba. Best-effort E `void`: desligar é higiene nossa — não pode virar
                 * falha e não pode fazer o apostador esperar por ela antes de voltar ao formulário.
                 */
                try {
                    void (conversationsClient as unknown as { shutdown?: () => Promise<void> })
                        .shutdown?.()
                        .catch(() => undefined);
                } catch {
                    /*
                     * `.catch()` só protege rejeição. Se `shutdown()` lançar de forma SÍNCRONA, a
                     * exceção escapa antes de o handler existir — e aqui estamos dentro de um
                     * caminho de tratamento de erro, então ela derrubaria o próprio tratamento.
                     * Best-effort de verdade: desligar é higiene nossa, nunca pode virar falha.
                     */
                }
                dispatch(addNotification(notifications.failedToInitSessionNotification("Couldn't load conversation")));
                dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
                /*
                 * 🔴 `{ ok: false }`, NÃO um `return` mudo.
                 *
                 * Este caminho trata a falha e devolve o apostador ao formulário — o que, visto de
                 * fora, é uma promise que RESOLVE. Quem chama isso na reconexão automática lia essa
                 * resolução como sucesso: zerava as tentativas, tirava o aviso de conexão e voltava
                 * sem emitir `reconnect_failed`. A recuperação desistia e a métrica criada para
                 * medir se ela funciona era zerada como se tivesse funcionado.
                 *
                 * Devolver o desfecho em vez de rejeitar é deliberado: rejeitar faria os DOIS outros
                 * chamadores (pré-chat e retomada de sessão) mostrarem uma segunda notificação em
                 * cima desta. Aqui só quem precisa do desfecho o consulta.
                 */
                return { ok: false } as const;
            }

            participants = await conversation.getParticipants();
            users = await Promise.all(participants.map(async (p) => p.getUser()));
            messages = (await conversation.getMessages(MESSAGES_LOAD_COUNT)).items;
        } catch (e) {
            log.error(`Something went wrong when initializing session: ${descreverErro(e)}`);
            throw e;
        }

        dispatch({
            type: ACTION_START_SESSION,
            payload: {
                token,
                conversationSid,
                conversationsClient,
                conversation,
                users,
                participants,
                messages,
                conversationState: conversation.state?.current,
                currentPhase: EngagementPhase.MessagingCanvas
            }
        });

        // Trava fixa do composer por ~3s ao iniciar a conversa (cobre o load do fluxo de boas-vindas).
        dispatch(updateWelcomeInputLock(true));

        // conversationSid vai adiante: o Tier 2 da reconexão precisa dele pra reconstruir a sessão
        initClientListeners(conversationsClient, dispatch, conversationSid);
        initConversationListener(conversation, dispatch);
        initMessagesListener(conversation, dispatch);
        initParticipantsListener(conversation, dispatch);

        return { ok: true } as const;
    };
}
